import React from 'react';
import ReactDOM from 'react-dom';
import {parse} from 'url';
import _ from 'lodash';
import PageLoader from './PageLoader';
import {createRouter} from './router';
import {initCounters} from './counters/metrika';
import {createPixel} from './counters/pixel';
import {getURL, toggleAdaptiveClass} from './utils';
import {rumFirstMeaningfulPaintInit} from './services/rum';

const as = getURL();
const [, hash = ''] = as.split('#');
const hostname = parse(window.location.href).hostname;

const {
    __DATA__: {
        err,
        useSSR,
        page,
        pageId,
        pathname,
        internalPathname,
        query,
        props = {},
        buildId,
        staticPath,
        metrikaCounters,
        pixelConfig,
        initialData,
        context,
    },
    __ENV__: env,
} = window;

const pageLoader = new PageLoader(context.locale, buildId, staticPath);
window.__LOADED_PAGES.forEach(({route, fn}) => {
    pageLoader.registerPage(route, fn);
});
delete window.__LOADED_PAGES;

window.__REGISTER_PAGE = pageLoader.registerPage.bind(pageLoader);

if (metrikaCounters) {
    initCounters(metrikaCounters);
}

if (pixelConfig) {
    createPixel(pixelConfig);
}

rumFirstMeaningfulPaintInit();

const constructablePages = _.get(initialData, ['constructablePages'], []);
const appContainer = document.getElementById('app');
const errorContainer = document.getElementById('error');
const actualPage = pageId ? `/_${pageId}` : page;
let router;
let errorPageData;
let DebugComponent;
let isInitialRender = useSSR;

function renderReactElement(reactEl, domEl) {
    if (isInitialRender) {
        ReactDOM.hydrate(reactEl, domEl);
        isInitialRender = false;
    } else {
        ReactDOM.render(reactEl, domEl);
    }
}

function doRender({page: {App, Component}, props: renderProps}) {
    const appProps = {
        Component,
        initialData,
        context: {...context, router, env},
        ...renderProps,
    };

    ReactDOM.unmountComponentAtNode(errorContainer);

    renderReactElement(<App {...appProps} />, appContainer);
}

function renderError(data) {
    if (process.env.NODE_ENV !== 'production' && process.env.RENDER_ENV !== 'production') {
        renderReactElement(<DebugComponent error={data.err} />, errorContainer);
        return;
    }

    doRender({...data, page: errorPageData});
}

function render(data) {
    if (data.err) {
        renderError(data);
        return;
    }

    try {
        doRender(data);
    } catch (error) {
        renderError({...data, err: error});
    }
}

export default async function init({DebugComponent: passedDebugComponent} = {}) {
    DebugComponent = passedDebugComponent;

    const [page, errorPage] = await Promise.all([
        useSSR ? pageLoader.loadPage(actualPage) : null,
        pageLoader.loadPage('/_error'),
    ]);

    errorPageData = errorPage;
    let currentPage = page || errorPage;
    let currentProps = props;

    router = createRouter(pathname, query, hash, as, hostname, constructablePages, {
        pageLoader,
        page: currentPage,
        errorPage,
        initialProps: currentProps,
        useSSR,
        context,
        internalPathname,
    });
    router.subscribe(({page, props, error: err} = {page: currentPage, props: currentProps}) => {
        toggleAdaptiveClass(router.internalPathname);
        render({page, props, err});
        currentPage = page;
        currentProps = props;
    });

    if (useSSR) {
        render({page, props, err});
    } else {
        router.replace(router.as);
    }
}
